import React, { useState, useEffect, useCallback } from 'react';
import { getAdminUsers, updateUserRole, AdminUser } from '../../services/adminUsersService';
import Loader from '../loader/Loader';
import Notification from '../toast/Notification';

const PER_PAGE = 20;

const formatPhone = (phone: string) => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('7')) return `+7 ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
  return phone;
};

const AdminUsersManagement: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [phoneSearch, setPhoneSearch] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [notification, setNotification] = useState<{ id: number; text: string; status: 'success' | 'error' | 'info' } | null>(null);

  const loadUsers = useCallback(async (p: number, phone: string) => {
    try {
      setLoading(true);
      const { users: data, total: totalCount } = await getAdminUsers({
        page: p,
        per_page: PER_PAGE,
        phone: phone || undefined,
      });
      setUsers(data);
      setTotal(totalCount);
    } catch (e) {
      console.error(e);
      showNotification('Ошибка загрузки списка пользователей', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers(page, phoneSearch);
  }, [page, phoneSearch, loadUsers]);

  const showNotification = (text: string, status: 'success' | 'error' | 'info') => {
    setNotification({ id: Date.now(), text, status });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSearch = () => {
    setPhoneSearch(phoneInput.trim());
    setPage(1);
  };

  const handleClearSearch = () => {
    setPhoneInput('');
    setPhoneSearch('');
    setPage(1);
  };

  const handleToggleAdmin = async (user: AdminUser) => {
    if (user.is_super_admin) return;
    try {
      setUpdatingId(user.id);
      await updateUserRole(user.id, !user.is_admin);
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, is_admin: !u.is_admin } : u))
      );
      showNotification(
        user.is_admin ? 'Права админа убраны' : 'Пользователь назначен админом',
        'success'
      );
    } catch (e) {
      showNotification('Ошибка обновления роли', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const hasSearch = phoneSearch.length > 0;

  if (loading && users.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {notification && (
        <Notification
          id={notification.id}
          text={notification.text}
          status={notification.status}
          duration={5000}
        />
      )}
      <p className="text-gray-600 dark:text-gray-400">
        Список пользователей. Назначьте права админа, чтобы открыть доступ к странице админки. Супер-админов здесь изменить нельзя. В базе сохраняется, кто добавил или убрал админа.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="admin-users-phone-search">
          Поиск по номеру телефона
        </label>
        <input
          id="admin-users-phone-search"
          type="text"
          placeholder="Поиск по телефону..."
          value={phoneInput}
          onChange={(e) => setPhoneInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 min-w-[200px]"
        />
        <button
          type="button"
          onClick={handleSearch}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Найти
        </button>
        {hasSearch && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Сбросить
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Имя</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Телефон</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Роль</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Действия</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {users.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  {hasSearch ? 'По запросу никого не найдено' : 'Нет пользователей'}
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{user.id}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{user.name || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatPhone(user.phone)}</td>
                  <td className="px-4 py-3">
                    {user.is_super_admin ? (
                      <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                        Супер-админ
                      </span>
                    ) : user.is_admin ? (
                      <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                        Админ
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                        Пользователь
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {user.is_super_admin ? (
                      <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleToggleAdmin(user)}
                        disabled={updatingId === user.id}
                        className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50"
                      >
                        {updatingId === user.id
                          ? '...'
                          : user.is_admin
                            ? 'Убрать права админа'
                            : 'Сделать админом'}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Показано {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} из {total}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Назад
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Страница {page} из {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Вперёд
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersManagement;
